export type WearableMetricId =
  | 'resting_heart_rate'
  | 'recovery_score'
  | 'hrv'
  | 'skin_temperature'
  | 'sleep_duration'
  | 'sleep_efficiency'
  | 'sleep_consistency'
  | 'respiratory_rate'
  | 'day_strain'
  | 'energy_burned'
  | 'sleep_stages'
  | 'workout_strain'
  | 'workout_duration';

export type WearableChartType = 'line' | 'bar' | 'sleep_stages';

export interface WearableLiteratureLink {
  label: string;
  url: string;
}

export interface WearableMetricDefinition {
  id: WearableMetricId;
  label: string;
  shortLabel: string;
  chartType: WearableChartType;
  accentColor: string;
  comparisonIsBetter: 'higher' | 'lower' | 'contextual';
  description: string;
  melioRead: string;
  caution: string;
  literatureLinks: WearableLiteratureLink[];
}

export const DEFAULT_DASHBOARD_METRIC_IDS: WearableMetricId[] = [
  'resting_heart_rate',
  'recovery_score',
  'sleep_duration',
  'sleep_efficiency',
];

export const ALL_WEARABLE_METRIC_IDS: WearableMetricId[] = [
  'resting_heart_rate',
  'recovery_score',
  'hrv',
  'skin_temperature',
  'sleep_duration',
  'sleep_efficiency',
  'sleep_consistency',
  'respiratory_rate',
  'day_strain',
  'energy_burned',
  'sleep_stages',
  'workout_strain',
  'workout_duration',
];

/**
 * `accentColor` is deliberately ONE hue across every metric. Each trend renders
 * as its own card with its own title (small multiples), so nothing is
 * distinguished by colour — the previous 13 arbitrary hues encoded no
 * information and only made the grid look like 13 unrelated products.
 * The exception is `sleep_stages`, the one stacked chart, whose chip matches
 * the sequential ramp its own segments use.
 */
export const WEARABLE_METRIC_DEFINITIONS: Record<WearableMetricId, WearableMetricDefinition> = {
  resting_heart_rate: {
    id: 'resting_heart_rate',
    label: 'Resting heart rate',
    shortLabel: 'Resting HR',
    chartType: 'line',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'lower',
    description: 'Resting heart rate is a simple recovery and load signal. It often drifts upward with illness, poor sleep, dehydration, stress, alcohol, or reduced fitness.',
    melioRead: 'Melio uses this as context rather than diagnosis. A sustained rise matters more than a single odd day.',
    caution: 'Resting heart rate is not a stand-alone cardiac assessment and should not override symptoms.',
    literatureLinks: [
      { label: 'PubMed: resting heart rate review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=resting+heart+rate+review' },
      { label: 'PubMed: heart rate and cardiovascular risk', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=resting+heart+rate+cardiovascular+risk+review' },
    ],
  },
  recovery_score: {
    id: 'recovery_score',
    label: 'Recovery score',
    shortLabel: 'Recovery',
    chartType: 'line',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'higher',
    description: 'Recovery score is a wearable composite signal that tries to summarise readiness from sleep, autonomic balance, and recent strain.',
    melioRead: 'Melio treats recovery as a pattern-recognition layer, not as a medical biomarker. It is useful for spotting sustained strain or mismatch between load and recovery.',
    caution: 'This is a device-defined score, not a clinical diagnosis or a validated disease marker.',
    literatureLinks: [
      { label: 'PubMed: athlete recovery monitoring review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=athlete+recovery+monitoring+review' },
      { label: 'PubMed: wearable recovery readiness review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=wearable+recovery+readiness+review' },
    ],
  },
  hrv: {
    id: 'hrv',
    label: 'Heart rate variability',
    shortLabel: 'HRV',
    chartType: 'line',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'contextual',
    description: 'Heart rate variability reflects beat-to-beat variation and is often used as a rough proxy for autonomic flexibility and recovery status.',
    melioRead: 'Melio looks at HRV over time, in context with sleep, symptoms, and training load. Stable baselines matter more than comparisons with other people.',
    caution: 'HRV can vary with device method, time of day, illness, alcohol, and training load. It should not be interpreted in isolation.',
    literatureLinks: [
      { label: 'PubMed: heart rate variability review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=heart+rate+variability+review' },
      { label: 'PubMed: HRV and training load review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=heart+rate+variability+training+load+review' },
    ],
  },
  skin_temperature: {
    id: 'skin_temperature',
    label: 'Skin temperature',
    shortLabel: 'Skin temp',
    chartType: 'line',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'contextual',
    description: 'Skin temperature is a wearable trend signal that can move with illness, environment, sleep disruption, alcohol, travel, and in some members cyclical hormonal changes.',
    melioRead: 'Melio uses skin temperature as a contextual signal. It becomes more useful when read alongside sleep, resting heart rate, HRV, and, where relevant, menstrual-cycle timing.',
    caution: 'Consumer wearable skin temperature is not the same as a calibrated core body temperature measurement.',
    literatureLinks: [
      { label: 'PubMed: wearable skin temperature review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=wearable+skin+temperature+review' },
      { label: 'PubMed: body temperature circadian review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=body+temperature+circadian+review' },
    ],
  },
  sleep_duration: {
    id: 'sleep_duration',
    label: 'Sleep duration',
    shortLabel: 'Sleep duration',
    chartType: 'line',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'contextual',
    description: 'Sleep duration captures how much time you were asleep each night. It is one of the cleanest longitudinal signals in wearable data.',
    melioRead: 'Melio uses this to spot whether under-sleep is transient or becoming a recurring baseline problem.',
    caution: 'Longer is not always better, and wearable sleep staging is imperfect. Duration still needs symptom context.',
    literatureLinks: [
      { label: 'PubMed: sleep duration review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=sleep+duration+review' },
      { label: 'PubMed: insufficient sleep health outcomes', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=insufficient+sleep+health+outcomes+review' },
    ],
  },
  sleep_efficiency: {
    id: 'sleep_efficiency',
    label: 'Sleep efficiency',
    shortLabel: 'Sleep efficiency',
    chartType: 'line',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'higher',
    description: 'Sleep efficiency estimates how much of time in bed was actually spent asleep rather than awake.',
    melioRead: 'Melio uses this to identify fragmented sleep and to support pattern-based decisions around sleep optimisation or onward review.',
    caution: 'Wearable estimates are directional rather than definitive and should not be treated as a sleep study.',
    literatureLinks: [
      { label: 'PubMed: sleep efficiency review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=sleep+efficiency+review' },
      { label: 'PubMed: insomnia sleep efficiency review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=insomnia+sleep+efficiency+review' },
    ],
  },
  sleep_consistency: {
    id: 'sleep_consistency',
    label: 'Sleep consistency',
    shortLabel: 'Sleep consistency',
    chartType: 'line',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'higher',
    description: 'Sleep consistency reflects how regular your sleep timing is across the week, rather than just how long you slept.',
    melioRead: 'Melio uses this because regular timing often explains recovery drift better than headline sleep duration alone.',
    caution: 'Consistency scores are wearable-derived and should be combined with real-world factors like travel, work schedule, and family life.',
    literatureLinks: [
      { label: 'PubMed: sleep regularity review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=sleep+regularity+review' },
      { label: 'PubMed: irregular sleep timing health review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=irregular+sleep+timing+health+review' },
    ],
  },
  respiratory_rate: {
    id: 'respiratory_rate',
    label: 'Respiratory rate',
    shortLabel: 'Respiratory rate',
    chartType: 'line',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'contextual',
    description: 'Respiratory rate during sleep is a relatively stable signal. A sustained rise can happen with infection, environmental change, or sleep disruption.',
    melioRead: 'Melio uses this as a context flag, especially when it moves together with sleep quality or resting heart rate.',
    caution: 'Respiratory rate is supportive context, not a diagnosis of lung, infection, or sleep-breathing disorders.',
    literatureLinks: [
      { label: 'PubMed: respiratory rate vital sign review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=respiratory+rate+vital+sign+review' },
      { label: 'PubMed: sleep respiratory rate monitoring review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=sleep+respiratory+rate+monitoring+review' },
    ],
  },
  day_strain: {
    id: 'day_strain',
    label: 'Day strain',
    shortLabel: 'Day strain',
    chartType: 'bar',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'contextual',
    description: 'Day strain is a device-level estimate of total cardiovascular load across the day.',
    melioRead: 'Melio reads strain as part of the load-versus-recovery picture rather than as a target to chase.',
    caution: 'This is a wearable scoring system, not a clinical measure of disease risk or overtraining.',
    literatureLinks: [
      { label: 'PubMed: training load monitoring review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=training+load+monitoring+review' },
      { label: 'PubMed: internal load athlete monitoring review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=internal+load+athlete+monitoring+review' },
    ],
  },
  energy_burned: {
    id: 'energy_burned',
    label: 'Energy burned',
    shortLabel: 'Energy burned',
    chartType: 'bar',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'contextual',
    description: 'Energy burned is a rough estimate of daily expenditure and can be useful when looked at with strain, workouts, and recovery.',
    melioRead: 'Melio uses this as contextual activity volume, not as a precise calorie prescription tool.',
    caution: 'Wearable calorie estimates can be materially wrong at the individual level.',
    literatureLinks: [
      { label: 'PubMed: wearable energy expenditure accuracy review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=wearable+energy+expenditure+accuracy+review' },
      { label: 'PubMed: physical activity energy expenditure review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=physical+activity+energy+expenditure+review' },
    ],
  },
  sleep_stages: {
    id: 'sleep_stages',
    label: 'Sleep stages',
    shortLabel: 'Sleep stages',
    chartType: 'sleep_stages',
    accentColor: '#2E5677',
    comparisonIsBetter: 'contextual',
    description: 'Sleep stages break sleep into light, deep, REM, and awake periods. These are useful as directional context over time.',
    melioRead: 'Melio looks for large pattern shifts rather than reading any one night literally. The best use is longitudinal context alongside duration and efficiency.',
    caution: 'Consumer wearables are not equivalent to polysomnography and can misclassify stages.',
    literatureLinks: [
      { label: 'PubMed: wearable sleep staging review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=wearable+sleep+staging+review' },
      { label: 'PubMed: sleep architecture review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=sleep+architecture+review' },
    ],
  },
  workout_strain: {
    id: 'workout_strain',
    label: 'Workout strain',
    shortLabel: 'Workout strain',
    chartType: 'bar',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'contextual',
    description: 'Workout strain estimates the internal load of recorded exercise sessions rather than the whole day.',
    melioRead: 'Melio uses this to see whether workouts are clustering with poor recovery or whether load has been unusually absent.',
    caution: 'Workout strain is device-defined and best used for internal comparison over time.',
    literatureLinks: [
      { label: 'PubMed: exercise training load review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=exercise+training+load+review' },
      { label: 'PubMed: session load monitoring review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=session+load+monitoring+review' },
    ],
  },
  workout_duration: {
    id: 'workout_duration',
    label: 'Workout duration',
    shortLabel: 'Workout duration',
    chartType: 'bar',
    accentColor: '#4F5D18',
    comparisonIsBetter: 'contextual',
    description: 'Workout duration captures how much recorded exercise time you logged on a given day.',
    melioRead: 'Melio uses workout duration as a volume signal, especially when paired with strain, recovery, and symptoms.',
    caution: 'More workout time is not automatically better and should be interpreted against goals, recovery, and real-world constraints.',
    literatureLinks: [
      { label: 'PubMed: exercise volume review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=exercise+volume+review' },
      { label: 'PubMed: exercise dose response review', url: 'https://pubmed.ncbi.nlm.nih.gov/?term=exercise+dose+response+review' },
    ],
  },
};
